import { useCallback, useEffect, useState } from 'react';

import { Box, TextField, Checkbox } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckBoxOutlineBlank, CheckBoxRounded, Search } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import SelectorLayout from './SelectorLayout';

import theme from '../../../../../../theme';
import { customFilterOptions } from '../../utils';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px'
  },
  option: {
    display: 'flex',
    width: '100%',
    heigh: '36px',
    alignItems: 'center',
    padding: '0',
    boxSizing: 'border-box',
    '& .description': {
      flexBasis: '70%',
      paddingLeft: '21px'
    },
    '& .count': {
      flexBasis: '15%',
      textAlign: 'center'
    },
    '& .checkbox-container': {
      display: 'flex',
      flexDirection: 'row-reverse',
      alignItems: 'flex-end',
      flexBasis: '15%',
      height: '37px',
      justifySelf: 'center',
      paddingRight: '12px'
    },
    '& .check': {
      '&.disabled': {
        display: 'none'
      }
    },
    '& .optionElement': {
      '&:first-child': {
        width: '20%'
      },
      '&:nth-child(2)': {
        width: '60%'
      },
      '&:nth-child(3)': {
        width: '10%',
        textAlign: 'center'
      },
      '&:last-child': {
        width: '10%'
      }
    }
  },
  checkbox: {
    color: theme.palette.info.dark,
    height: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    '&.disabled': {
      display: 'none'
    }
  },
  textField: {
    minWidth: '397px',
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.common.white,
      height: '45px'
    },
    '& .MuiInputBase-adornedStart > span.MuiAutocomplete-tag': {
      paddingLeft: '10px'
    },
    '& .MuiAutocomplete-endAdornment': {
      paddingRight: '18px',
      '& .MuiAutocomplete-popupIndicator': {
        pointerEvents: 'none',
        margin: theme.spacing(0, 0.5),
        transform: 'none'
      }
    }
  }
}));

export type SearchboxElementType = {
  number?: string;
  name: string;
  count: number;
  isDisabled?: boolean;
  prevSelected?: boolean;
};

interface Props {
  [key: string]: any;
  onSelect: (selected: any) => void;
  options: SearchboxElementType[];
  placeholder?: string;
  isLoading?: boolean;
  value: SearchboxElementType[];
  isDisabled?: boolean;
}

const SearchboxSelector = ({
  options,
  onSelect,
  placeholder,
  isLoading = false,
  value,
  isDisabled,
  ...props
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SearchboxElementType[]>(value);
  const [inputValue, setInputValue] = useState('');

  props.onInputChange = (event: any, value: any, reason: any) => {
    if (event && event.type === 'blur') {
      setInputValue('');
    } else if (reason !== 'reset') {
      setInputValue(value);
    }
  };

  props.inputValue = inputValue;

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const openDropdown = () => {
    if (!isDisabled) {
      setOpen(true);
    }
  };

  const onChange = (event: any, values: any) => {
    setSelected(values);
  };

  const selectHandler = useCallback(() => {
    onSelect(selected);
    setOpen(false);
  }, [onSelect, selected]);

  const cancelHandler = useCallback(() => {
    setSelected(value);
    setOpen(false);
    onSelect(value);
  }, [onSelect, value]);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      renderTags={() => null}
      id="searchbox-selector"
      options={options}
      getOptionLabel={(option) => option.name}
      filterOptions={customFilterOptions}
      open={open}
      getOptionDisabled={(option) => Boolean(option.isDisabled)}
      renderOption={({ number, name, count, isDisabled }, { selected }) => (
        <Box key={name} className={classes.option}>
          <Box className="optionElement">{number}</Box>
          <Box className="optionElement">{name}</Box>
          <Box className="optionElement">{count}</Box>
          <Box className="optionElement">
            <Checkbox
              checked={selected}
              checkedIcon={<CheckBoxRounded className={classes.checkbox} />}
              icon={<CheckBoxOutlineBlank className={classes.checkbox} />}
              className={`check${isDisabled ? ' disabled' : ''}`}
            />
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          className={classes.textField}
          onClick={openDropdown}
          {...params}
          placeholder={placeholder}
          variant="outlined"
        />
      )}
      PaperComponent={({ children, ...props }) => (
        <SelectorLayout isLoading={isLoading} onClose={cancelHandler} onSelect={selectHandler} {...props}>
          {children}
        </SelectorLayout>
      )}
      popupIcon={<Search />}
      value={selected}
      disabled={isDisabled}
      onFocus={openDropdown}
      onChange={onChange}
      onBlur={cancelHandler}
      {...props}
    />
  );
};

export default SearchboxSelector;
